import React, { useCallback, useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { getAgents } from "../../../api/agent";
import { addLead } from "../../../api/lead";
import InfiniteScrollSelect from "../../../components/common/InfiniteScrollSelect";
import AddressAutocomplete from "../../../components/common/AddressAutocomplete";
import { normalizeEmail, parseAddressParts } from "../../../helper/function";

// Sources an agent can pick when typing a seller lead in by hand. The values are
// Lead.SOURCE_CHOICES (lead_management/models/leads.py) — "social_media" is where
// the Meta campaign leads land, "rea"/"domain" the portal seller enquiries.
const SOURCE_OPTIONS = [
  { value: "social_media", label: "Social Media (Meta)" },
  { value: "rea", label: "REA.com.au" },
  { value: "domain", label: "Domain.com.au" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "cold_call", label: "Cold Call" },
  { value: "ofi", label: "Open For Inspection" },
  // Christian Gravias, DOMA 13375194285, 24 Oct 2026.
  { value: "google", label: "Google" },
  { value: "ai_chat", label: "ChatGPT / Claude / Gemini / Grok" },
  { value: "other", label: "Other" },
];

// Sale or rental (DOMA 13375194285). Lead.transaction_type; rental leads are
// kept out of the sales analytics on the backend (lead_types.RENTAL_Q).
export const TRANSACTION_TYPES = [
  { value: "sale", label: "Sale" },
  { value: "rental", label: "Rental" },
];

// Only offered when the form was opened from Re-listing Opportunities.
const RELISTING_SOURCE = { value: "relisting", label: "Re-listing Opportunity" };

// Where the lead lands on Prospects, offered when `choosePlacement` is set
// (Paul Jimoulis CRM-2026-00116, 20 Oct 2026: "Make it that you can choose
// where you want the lead to sit, such as new cold warm hot"). New is the New
// column; Cold / Warm / Hot mark it called, so it sits in Connected with that
// temperature, as if the agent had pressed Called and picked one.
export const PLACEMENTS = [
  { value: "new", label: "New", on: "bg-slate-600 text-white border-slate-600" },
  { value: "cold", label: "Cold", on: "bg-[#0EA5E9] text-white border-[#0EA5E9]" },
  { value: "warm", label: "Warm", on: "bg-[#F97316] text-white border-[#F97316]" },
  { value: "hot", label: "Hot", on: "bg-[#22C55E] text-white border-[#22C55E]" },
];

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  // Jointly owned properties (George Bottas CRM-2026-00114, 21 Oct 2026).
  second_owner_name: "",
  second_owner_phone: "",
  second_owner_email: "",
  property_address: "",
  // Structured address parts captured when the agent picks an autocomplete
  // suggestion, so the backend can dedup + create a property profile for the
  // seller lead (needs a suburb). DOMA 13121348574.
  property_suburb: "",
  property_state: "",
  property_postcode: "",
  source: "social_media",
  market: "off_market",
  agent: "",
  initial_notes: "",
  placement: "new",
  transaction_type: "sale",
};

/**
 * "Add Lead" — lets an agent (or Christian, working the social-media inbox) type a
 * seller lead straight into the CRM and, in the same step, hand it to whichever
 * agent should chase it (Christian Gravias, Monday 12817261831, 2026-08-17).
 *
 * Saves a Lead with lead_type='owner' so it lands in the Seller Leads lists rather
 * than the Buyer Enquiries holding area.
 *
 * `initial` pre-fills the form (Re-listing Opportunities passes the address and
 * the Homer details as a note) and `onSaved` receives the new lead's id.
 * `choosePlacement` adds the New / Cold / Warm / Hot picker; `successMessage`
 * may be a function of the picked placement's label.
 */
const AddSellerLeadModal = ({
  isOpen,
  onClose,
  onSaved,
  defaultMarket = "off_market",
  initial = null,
  successMessage = "Lead added.",
  choosePlacement = false,
}) => {
  const [form, setForm] = useState({ ...EMPTY, market: defaultMarket });
  const [saving, setSaving] = useState(false);
  const [showSecondOwner, setShowSecondOwner] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...EMPTY, market: defaultMarket, ...(initial || {}) });
    // Open when the caller already knows a second owner (Re-listing's expired listings sheet).
    setShowSecondOwner(Boolean(initial && (initial.second_owner_name || initial.second_owner_phone)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultMarket]);

  // Agents-only list, searched server-side as the user types.
  const fetchAgentOptions = useCallback(async (page, pageSize, search = "") => {
    const res = await getAgents({ page, page_size: pageSize, search, role: "agent" });
    const data = res?.data ?? {};
    return {
      results: data.results ?? (Array.isArray(data) ? data : []),
      next: Boolean(data.next),
    };
  }, []);

  if (!isOpen) return null;

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Enter the lead's name.");
      return;
    }
    // Clean up the email before validating/sending: a pasted "Name <addr>"
    // (copying a sender from Outlook/Gmail) or a stray invisible character
    // would otherwise be rejected by the backend as invalid. DOMA 13121356668.
    const email = normalizeEmail(form.email);
    if (!form.phone.trim() && !email) {
      toast.error("Enter a phone number or an email so the lead can be contacted.");
      return;
    }
    const secondOwnerEmail = normalizeEmail(form.second_owner_email);
    const placement = PLACEMENTS.find((p) => p.value === form.placement) || PLACEMENTS[0];
    const called = choosePlacement && placement.value !== "new";
    setSaving(true);
    try {
      const res = await addLead({
        name: form.name.trim(),
        full_name: form.name.trim(),
        phone: form.phone.trim(),
        email,
        second_owner_name: form.second_owner_name.trim(),
        second_owner_phone: form.second_owner_phone.trim(),
        second_owner_email: secondOwnerEmail,
        property_address: form.property_address.trim(),
        owner_subject_address: form.property_address.trim(),
        // Structured parts (from the address autocomplete) let the backend
        // dedup + create a property profile for this seller lead. Harmless when
        // blank — the backend skips profile creation without a suburb.
        property_suburb: form.property_suburb.trim(),
        property_state: form.property_state.trim(),
        property_postcode: form.property_postcode.trim(),
        source: form.source,
        market: form.market,
        transaction_type: form.transaction_type,
        lead_type: "owner",
        stage: "New Lead",
        // Blank means "leave unassigned" — admins park social-media leads here and
        // assign them later from the Seller Leads table.
        agent: form.agent || null,
        initial_notes: form.initial_notes.trim(),
        ...(called ? { temperature: placement.value, last_contact_date: new Date().toISOString() } : {}),
      });
      toast.success(typeof successMessage === "function" ? successMessage(placement.label) : successMessage);
      if (onSaved) await onSaved(res?.data?.id);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add the lead.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]";
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-3 overflow-y-auto">
      <form
        onSubmit={handleSubmit}
        aria-label="Add Lead"
        className="bg-white rounded-xl shadow-xl w-full max-w-lg my-6"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#2D3441]">Add Lead</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div>
            <span className={labelClass} id="add-lead-transaction">Sale or rental</span>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-labelledby="add-lead-transaction">
              {TRANSACTION_TYPES.map((t) => {
                const on = form.transaction_type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setForm((prev) => ({ ...prev, transaction_type: t.value }))}
                    className={`py-2 rounded-lg border text-sm font-semibold transition-colors ${
                      on ? "bg-[#55C5D0] text-white border-[#55C5D0]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {form.transaction_type === "rental" && (
              <p className="text-[11px] text-gray-400 mt-1">Rental leads are not counted in the sales analytics.</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Name *</label>
            <input className={inputClass} value={form.name} onChange={set("name")} placeholder="Jane Smith" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Phone</label>
              <input className={inputClass} value={form.phone} onChange={set("phone")} placeholder="0400 000 000" />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input className={inputClass} value={form.email} onChange={set("email")} placeholder="jane@example.com" />
            </div>
          </div>

          {showSecondOwner ? (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#2D3441]">Second owner</span>
                <button
                  type="button"
                  onClick={() => {
                    setShowSecondOwner(false);
                    setForm((p) => ({ ...p, second_owner_name: "", second_owner_phone: "", second_owner_email: "" }));
                  }}
                  className="text-xs font-semibold text-gray-400 hover:text-gray-600"
                >
                  Remove
                </button>
              </div>
              <div>
                <label className={labelClass}>Name</label>
                <input className={`${inputClass} bg-white`} value={form.second_owner_name} onChange={set("second_owner_name")} placeholder="John Smith" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Phone</label>
                  <input className={`${inputClass} bg-white`} value={form.second_owner_phone} onChange={set("second_owner_phone")} placeholder="0400 000 000" />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input className={`${inputClass} bg-white`} value={form.second_owner_email} onChange={set("second_owner_email")} placeholder="john@example.com" />
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowSecondOwner(true)}
              className="text-xs font-semibold text-[#2b9aa4] hover:underline flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add a second owner
            </button>
          )}

          <div>
            <label className={labelClass}>Property address</label>
            {/* Autocomplete captures a structured suburb so the backend can give
                the seller lead a searchable property profile. Picking a
                suggestion fills the suburb/state/postcode; typing by hand still
                works (the backend just skips profile creation without a suburb).
                DOMA 13121348574. */}
            <AddressAutocomplete
              value={form.property_address}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  property_address: e.target.value,
                  // A hand-edit invalidates the parts captured from a prior pick.
                  property_suburb: "",
                  property_state: "",
                  property_postcode: "",
                }))
              }
              onSelect={(sug) => {
                const desc = sug.description || sug.text || "";
                const fallback = parseAddressParts(desc);
                setForm((prev) => ({
                  ...prev,
                  property_address: desc || prev.property_address,
                  property_suburb: sug.suburb || fallback.suburb || "",
                  property_state: sug.state || fallback.state || "",
                  property_postcode: sug.postcode || fallback.postcode || "",
                }));
              }}
              placeholder="12 Example St, Northcote VIC 3070"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Source</label>
              <select className={inputClass} value={form.source} onChange={set("source")}>
                {(form.source === RELISTING_SOURCE.value ? [RELISTING_SOURCE, ...SOURCE_OPTIONS] : SOURCE_OPTIONS).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>List</label>
              <select className={inputClass} value={form.market} onChange={set("market")}>
                <option value="off_market">Off Market Leads</option>
                <option value="on_market">On Market Leads</option>
              </select>
            </div>
          </div>

          {choosePlacement && (
            <div>
              <span className={labelClass} id="add-lead-placement">Where should this lead sit?</span>
              <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-labelledby="add-lead-placement">
                {PLACEMENTS.map((p) => {
                  const on = form.placement === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setForm((prev) => ({ ...prev, placement: p.value }))}
                      className={`py-2 rounded-lg border text-sm font-semibold transition-colors ${
                        on ? p.on : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {form.placement === "new"
                  ? "Prospects > New, not called yet."
                  : "Prospects > Connected, marked as called."}
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>Assign an agent</label>
            <InfiniteScrollSelect
              value={form.agent}
              onChange={(val) => setForm((p) => ({ ...p, agent: val }))}
              placeholder="Leave unassigned"
              fetchOptions={fetchAgentOptions}
              getLabel={(a) => a.name || a.email}
              getValue={(a) => a.id}
            />
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              className={`${inputClass} min-h-[70px]`}
              value={form.initial_notes}
              onChange={set("initial_notes")}
              placeholder="What did they say?"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {saving ? "Saving..." : "Add Lead"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddSellerLeadModal;
